import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  generateProspectDetailsUrl,
  generateBaseUrl,
  getProspectDetailsRouteInfo,
  extractTabFromUrl,
  ValidTab,
} from '../utils/prospect-details-url';

/**
 * Custom hook for managing prospect details modal URLs
 * Automatically handles URL updates when opening/closing modal and changing tabs
 *
 * @returns Object with functions to update URL for opening modal, closing modal, and changing tabs
 */
export const useProspectDetailsUrl = () => {
  const history = useHistory();
  const location = useLocation();

  /**
   * Updates URL when opening prospect details modal
   * @param entityId - Prospect ID on most routes; on dialer call logs use **CallLog.id**
   * @param tab - Tab name (defaults to 'activity')
   */
  const openProspectDetails = useCallback(
    (entityId: number, tab?: ValidTab) => {
      // If no tab provided, determine default based on route type
      let defaultTab: ValidTab = 'activity';
      if (!tab) {
        const routeInfo = getProspectDetailsRouteInfo(location.pathname);
        // Tasks route defaults to 'contacts', others default to 'activity'
        defaultTab = routeInfo.viewType === 'tasks' ? 'contacts' : 'activity';
      }

      const newPathname = generateProspectDetailsUrl(
        location.pathname,
        entityId,
        tab || defaultTab,
      );
      // Preserve query parameters and hash fragment when opening modal
      history.replace({
        pathname: newPathname,
        search: location.search,
        hash: location.hash,
      });
    },
    [history, location.pathname, location.search, location.hash],
  );

  /**
   * Updates URL when closing prospect details modal
   */
  const closeProspectDetails = useCallback(() => {
    const baseUrl = generateBaseUrl(location.pathname);
    // Preserve query parameters and hash fragment when closing
    history.replace({
      pathname: baseUrl,
      search: location.search,
      hash: location.hash,
    });
  }, [history, location.pathname, location.search, location.hash]);

  /**
   * Updates URL when changing tabs in prospect details modal
   * @param entityId - Prospect ID on most routes; on dialer call logs use **CallLog.id**
   * @param tab - New tab name
   */
  const changeTab = useCallback(
    (entityId: number, tab: ValidTab) => {
      const newPathname = generateProspectDetailsUrl(
        location.pathname,
        entityId,
        tab,
      );
      // Preserve query parameters and hash fragment when changing tabs
      history.replace({
        pathname: newPathname,
        search: location.search,
        hash: location.hash,
      });
    },
    [history, location.pathname, location.search, location.hash],
  );

  /**
   * Gets the current route info
   */
  const getRouteInfo = useCallback(
    () => getProspectDetailsRouteInfo(location.pathname),
    [location.pathname],
  );

  /**
   * Gets the tab from the current URL
   */
  const tabFromUrl = useMemo(() => extractTabFromUrl(location.pathname), [
    location.pathname,
  ]);

  return {
    openProspectDetails,
    closeProspectDetails,
    changeTab,
    getRouteInfo,
    tabFromUrl,
  };
};
